/**
 * Configuration Service
 * Manages loading and accessing lookup data from JSON configuration files
 */

// Import JSON files
import grantTypesData from '@/config/grantTypes.json';
import donorsData from '@/config/donors.json';
import budgetIdsData from '@/config/budgetIds.json';
import departmentsData from '@/config/departments.json';
import type { DepartmentOption } from '@/types/invoice';

// Type definitions for configuration data
export interface GrantType {
  id: string;
  label: string;
  value: string;
  description: string;
}

export interface Donor {
  id: string;
  name: string;
  shortName: string;
  type: string;
  category: string;
  description: string;
  grantType: string;
}

export interface BudgetId {
  id: string;
  label: string;
  description: string;
  department: string;
  isActive: boolean;
}


class ConfigService {
  private grantTypes: GrantType[] = grantTypesData;
  private donors: Donor[] = donorsData;
  private budgetIds: BudgetId[] = budgetIdsData;
  private departments: DepartmentOption[] = departmentsData;

  /**
   * Get all grant types
   */
  getGrantTypes(): GrantType[] {
    return this.grantTypes;
  }

  /**
   * Get grant type by ID
   */
  getGrantTypeById(id: string): GrantType | undefined {
    return this.grantTypes.find(gt => gt.id === id);
  }

  /**
   * Get grant type by value
   */
  getGrantTypeByValue(value: string): GrantType | undefined {
    return this.grantTypes.find(gt => gt.value === value);
  }

  /**
   * Get all donors
   */
  getDonors(): Donor[] {
    return this.donors;
  }

  /**
   * Get donors by type
   */
  getDonorsByType(type: string): Donor[] {
    return this.donors.filter(donor => donor.type === type);
  }

  /**
   * Get donors by grant type (new method for dependent dropdown)
   */
  getDonorsByGrantType(grantType: string): Donor[] {
    return this.donors.filter(donor => donor.grantType === grantType);
  }

  /**
   * Get donor by ID
   */
  getDonorById(id: string): Donor | undefined {
    return this.donors.find(donor => donor.id === id);
  }

  /**
   * Get donor name by ID (convenience method)
   */
  getDonorNameById(id?: string): string {
    if (!id) return 'Not set';
    const donor = this.getDonorById(id);
    return donor ? donor.name : `Donor ID: ${id}`;
  }

  /**
   * Get all budget IDs
   */
  getBudgetIds(): BudgetId[] {
    return this.budgetIds.filter(budget => budget.isActive);
  }

  /**
   * Get budget IDs by department
   */
  getBudgetIdsByDepartment(department: string): BudgetId[] {
    return this.budgetIds.filter(budget => 
      budget.department === department && budget.isActive
    );
  }

  /**
   * Get budget ID by ID
   */
  getBudgetIdById(id: string): BudgetId | undefined {
    return this.budgetIds.find(budget => budget.id === id && budget.isActive);
  }

  /**
   * Get budget label by ID (convenience method)
   */
  getBudgetLabelById(id?: number | string): string {
    if (!id) return 'Not set';
    const budget = this.getBudgetIdById(id.toString());
    return budget ? budget.label : `Budget ID: ${id}`;
  }

  /**
   * Get all departments
   */
  getDepartments(): DepartmentOption[] {
    return this.departments.filter(dept => dept.isActive);
  }

  /**
   * Get department by ID
   */
  getDepartmentById(id: string): DepartmentOption | undefined {
    return this.departments.find(dept => dept.id === id && dept.isActive);
  }

  /**
   * Get department name by ID (convenience method)
   */
  getDepartmentNameById(id?: string): string {
    if (!id) return 'Not set';
    const department = this.getDepartmentById(id);
    return department ? department.label : `Department ID: ${id}`;
  }

  /**
   * Get unique donor types
   */
  getDonorTypes(): string[] {
    return [...new Set(this.donors.map(donor => donor.type))];
  }

  /**
   * Get unique donor categories
   */
  getDonorCategories(): string[] {
    return [...new Set(this.donors.map(donor => donor.category))];
  }

  /**
   * Search donors by name or short name
   */
  searchDonors(query: string): Donor[] {
    const lowercaseQuery = query.toLowerCase();
    return this.donors.filter(donor => 
      donor.name.toLowerCase().includes(lowercaseQuery) ||
      donor.shortName.toLowerCase().includes(lowercaseQuery)
    );
  }

  /**
   * Get summary statistics
   */
  getConfigSummary() {
    return {
      grantTypes: this.grantTypes.length,
      donors: this.donors.length,
      budgetIds: this.budgetIds.filter(b => b.isActive).length,
      departments: this.departments.filter(d => d.isActive).length,
      donorTypes: this.getDonorTypes().length
    };
  }

  /**
   * Reload configuration data (for future use with dynamic loading)
   */
  async reloadConfig(): Promise<void> {
    // This method can be extended to dynamically reload config from API
    // For now, it's a placeholder for future functionality
    console.log('Config reload requested - using static imports');
  }

  /**
   * Validate configuration data
   */
  validateConfig(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate grant types
    if (this.grantTypes.length === 0) {
      errors.push('No grant types configured');
    }

    // Validate donors have unique IDs
    const donorIds = this.donors.map(d => d.id);
    if (new Set(donorIds).size !== donorIds.length) {
      errors.push('Duplicate donor IDs found');
    }

    // Validate budget IDs have unique IDs
    const budgetIds = this.budgetIds.map(b => b.id);
    if (new Set(budgetIds).size !== budgetIds.length) {
      errors.push('Duplicate budget IDs found');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const configService = new ConfigService();
export default configService;